import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { ChevronLeft, ImageIcon, LayoutGrid, ListIcon } from "lucide-react";

import { SegmentedControl, StatusPill } from "@/components/common";
import { CampaignOverviewSection } from "@/components/campaign-review/campaign-overview-section";
import { GeneratePlannedPostSheet } from "@/components/campaign-review/generate-planned-post-sheet";
import { PlannedPostCard } from "@/components/campaign-review/planned-post-card";
import { useAdminCampaignReviewData } from "@/components/campaign-review/use-admin-campaign-review-data";
import { MaterializedPromptPanel } from "@/components/llm/materialized-prompt-panel";
import {
  buildPlannedPostDetailPath,
  getPostTypeLabel,
  PLANNED_POST_STATUS_CONFIG,
} from "@/components/campaign-review/utils";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import { formatShortDate } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { SceneTemplatePreview } from "@/pages/social-post-planner/components/scene-template-preview";
import { formatDate } from "@/pages/social-post-planner/utils";
import type { CampaignReviewPlannedPost } from "@/components/campaign-review/types";

import { CAMPAIGN_STATUS_CONFIG } from "./campaigns";

export const CAMPAIGN_BOARD_VIEWS = [
  { key: "queue", label: "Review Queue" },
  { key: "cards", label: "Visual Cards" },
] as const;

type CampaignBoardView = (typeof CAMPAIGN_BOARD_VIEWS)[number]["key"];

export const getCampaignBoardViewLabels = (): string[] =>
  CAMPAIGN_BOARD_VIEWS.map((view) => view.label);

export const CAMPAIGN_BOARD_FILTERS = [
  { key: "all", label: "All" },
  { key: "needs_attention", label: "Needs attention" },
  { key: "ready", label: "Ready" },
  { key: "in_progress", label: "In progress" },
] as const;

type CampaignBoardFilter = (typeof CAMPAIGN_BOARD_FILTERS)[number]["key"];

export const getCampaignBoardFilterLabels = (): string[] =>
  CAMPAIGN_BOARD_FILTERS.map((filter) => filter.label);

export const getCampaignBoardFilterKey = (post: Pick<CampaignReviewPlannedPost, "status">) => {
  if (post.status === "failed") {
    return "needs_attention";
  }

  if (post.status === "generated") {
    return "ready";
  }

  if (post.status === "scheduled" || post.status === "generating") {
    return "in_progress";
  }

  return "all";
};

export const filterCampaignBoardPosts = (
  posts: CampaignReviewPlannedPost[],
  filter: CampaignBoardFilter,
) =>
  filter === "all" ? posts : posts.filter((post) => getCampaignBoardFilterKey(post) === filter);

export function CampaignDetailPage() {
  const { id: brandId, campaignId } = useParams<{ id: string; campaignId: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [selectedScheduledPostId, setSelectedScheduledPostId] = useState<string | null>(null);
  const [additionalDirection, setAdditionalDirection] = useState("");
  const [expandedPostIds, setExpandedPostIds] = useState<string[]>([]);
  const [newAttemptSheetOpen, setNewAttemptSheetOpen] = useState(false);
  const [boardView, setBoardView] = useState<CampaignBoardView>("queue");
  const [boardFilter, setBoardFilter] = useState<CampaignBoardFilter>("all");

  const { brandQuery, campaignQuery, brand, campaign, tasks, campaignBrief, plannedPosts } =
    useAdminCampaignReviewData({ brandId, campaignId });

  const enqueueMutation = trpc.admin.brands.enqueueSocialPostGeneration.useMutation({
    onSuccess: async (data) => {
      setNewAttemptSheetOpen(false);
      await utils.admin.brands.socialPostGenerationTasks.invalidate({ brandId: brandId ?? "" });
      await utils.admin.campaigns.get.invalidate({
        brandId: brandId ?? "",
        campaignId: campaignId ?? "",
      });

      if (selectedScheduledPostId) {
        navigate(
          `${buildPlannedPostDetailPath(brandId ?? "", campaignId ?? "", selectedScheduledPostId)}?taskId=${data.generationTaskId}`,
        );
      }
    },
  });
  const approveMutation = trpc.campaign.approve.useMutation({
    onSuccess: async () => {
      await utils.admin.campaigns.get.invalidate({
        brandId: brandId ?? "",
        campaignId: campaignId ?? "",
      });
      await utils.admin.campaigns.list.invalidate({ brandId: brandId ?? "" });
    },
  });
  const rejectMutation = trpc.campaign.reject.useMutation({
    onSuccess: async () => {
      await utils.admin.campaigns.list.invalidate({ brandId: brandId ?? "" });
    },
  });

  const selectedScheduledPost =
    plannedPosts.find((post) => post.id === selectedScheduledPostId) ?? null;
  const selectedScheduledPostHasActiveRun = Boolean(
    selectedScheduledPost?.output.latestAttempt &&
    (selectedScheduledPost.output.latestAttempt.status === "queued" ||
      selectedScheduledPost.output.latestAttempt.status === "processing"),
  );
  const plannerDisabled =
    brand?.status !== "scanned" ||
    enqueueMutation.isPending ||
    !selectedScheduledPost ||
    selectedScheduledPostHasActiveRun;

  const readyCount = plannedPosts.filter((post) => post.status === "generated").length;
  const inFlightCount = plannedPosts.filter(
    (post) => post.status === "scheduled" || post.status === "generating",
  ).length;
  const failedCount = plannedPosts.filter((post) => post.status === "failed").length;
  const filteredPosts = useMemo(
    () => filterCampaignBoardPosts(plannedPosts, boardFilter),
    [boardFilter, plannedPosts],
  );

  const handleSubmit = () => {
    if (!selectedScheduledPost || !campaign) {
      return;
    }

    enqueueMutation.mutate({
      brandId: brandId ?? "",
      campaignId: campaignId ?? "",
      scheduledPostId: selectedScheduledPost.id,
      request: {
        type: "social_post.request",
        version: 3,
        sourceBrief: {
          version: 1,
          origin: "campaign",
          campaignId: campaign.id,
          campaignName: campaign.name,
          campaignBrief,
          slot: selectedScheduledPost.slot,
          additionalDirection: additionalDirection.trim() || null,
        },
        platform: selectedScheduledPost.slot.platform,
        postType: selectedScheduledPost.slot.postType,
        variantCount: 1,
      },
    });
  };

  const nextSuggestedPostId = useMemo(
    () =>
      plannedPosts.find((post) => post.status === "failed")?.id ??
      plannedPosts.find((post) => post.status === "scheduled" || post.status === "generating")
        ?.id ??
      plannedPosts[0]?.id ??
      null,
    [plannedPosts],
  );

  if (!brandId || !campaignId) {
    return null;
  }

  if (campaignQuery.isPending || brandQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (campaignQuery.error || !campaign) {
    return <Navigate to={`/brands/${brandId}/campaigns`} replace />;
  }

  if (!brand) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <p className="text-lg font-medium">Failed to load brand</p>
        <p className="text-sm text-muted-foreground">{brandQuery.error?.message}</p>
      </div>
    );
  }

  const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.status];
  const startDate = formatShortDate(campaign.startDate);
  const endDate = formatShortDate(campaign.endDate);

  return (
    <div className="space-y-8 px-6 py-6 lg:px-8 xl:px-10">
      <header className="space-y-6 border-b border-border/60 pb-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-3 w-fit gap-2 text-muted-foreground/90"
              asChild
            >
              <Link to={`/brands/${brandId}/campaigns`}>
                <ChevronLeft className="size-4" />
                Back to campaigns
              </Link>
            </Button>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill
                  label={statusConfig?.label ?? campaign.status}
                  className={statusConfig?.className}
                />
                <span className="text-xs uppercase tracking-[0.14em] text-foreground/55">
                  {brand.sourceDomain} - {startDate} to {endDate}
                </span>
              </div>
              <div className="space-y-1">
                <h1 className="max-w-5xl text-balance font-serif text-[2.35rem] font-semibold tracking-[-0.03em] text-foreground sm:text-[2.8rem] xl:text-[3.15rem]">
                  {campaign.name}
                </h1>
                <p className="max-w-5xl text-pretty text-[15px] leading-7 text-foreground/72">
                  {campaignBrief.summary}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <Link to="/runs">
                <ListIcon className="size-4" />
                All Runs
              </Link>
            </Button>
            <GeneratePlannedPostSheet
              open={newAttemptSheetOpen}
              onOpenChange={(open) => {
                setNewAttemptSheetOpen(open);
                if (open && !selectedScheduledPostId) {
                  setSelectedScheduledPostId(nextSuggestedPostId);
                }
              }}
              triggerLabel="Generate Planned Post"
              campaignName={campaign.name}
              campaignBrief={campaignBrief}
              plannedPosts={plannedPosts}
              selectedScheduledPostId={selectedScheduledPostId}
              onSelectScheduledPostId={setSelectedScheduledPostId}
              additionalDirection={additionalDirection}
              onAdditionalDirectionChange={setAdditionalDirection}
              brandStatus={brand.status}
              selectedScheduledPostHasActiveRun={selectedScheduledPostHasActiveRun}
              disabled={plannerDisabled}
              isPending={enqueueMutation.isPending}
              errorMessage={enqueueMutation.error?.message}
              onSubmit={handleSubmit}
            />
            {campaign.status === "pending_review" ? (
              <>
                <Button
                  size="sm"
                  onClick={() =>
                    brandId && campaignId && approveMutation.mutate({ brandId, id: campaignId })
                  }
                  disabled={approveMutation.isPending}
                >
                  Approve Campaign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    brandId && campaignId && rejectMutation.mutate({ brandId, id: campaignId })
                  }
                  disabled={rejectMutation.isPending}
                >
                  Reject Campaign
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 text-sm text-muted-foreground">
          <Badge
            variant="outline"
            className="rounded-full border-border/80 bg-card/90 px-3 py-1.5 text-[11px] text-foreground/78"
          >
            {plannedPosts.length} planned posts
          </Badge>
          <Badge
            variant="outline"
            className="rounded-full border-border/80 bg-card/90 px-3 py-1.5 text-[11px] text-foreground/78"
          >
            {readyCount} ready
          </Badge>
          <Badge
            variant="outline"
            className="rounded-full border-border/80 bg-card/90 px-3 py-1.5 text-[11px] text-foreground/78"
          >
            {inFlightCount} in progress
          </Badge>
          {failedCount > 0 ? (
            <Badge
              variant="outline"
              className="rounded-full border-border/80 bg-card/90 px-3 py-1.5 text-[11px] text-foreground/78"
            >
              {failedCount} need attention
            </Badge>
          ) : null}
          <span className="ml-1 text-xs uppercase tracking-[0.14em] text-foreground/58">
            {tasks.length} generation attempts
          </span>
        </div>

        {campaign.status === "pending_review" ? (
          <div className="rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-4 text-sm text-foreground/80">
            <p className="font-medium text-foreground">Campaign review in progress</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The first post is generated as preview. Brief and schedule edits are allowed during
              review only. Approval activates the campaign and unlocks the remaining post
              generation.
            </p>
          </div>
        ) : null}
      </header>

      <section className="space-y-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <ListIcon className="h-5 w-5 text-foreground/55" aria-hidden="true" />
              Planned Post Review Queue
            </h2>
            <p className="max-w-3xl text-[15px] leading-7 text-foreground/68">
              Triage posts by status and next action first. Switch to visual cards when creative
              comparison matters more than throughput.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <SegmentedControl
              options={CAMPAIGN_BOARD_FILTERS.map((filter) => ({
                value: filter.key,
                label: filter.label,
              }))}
              value={boardFilter}
              onChange={setBoardFilter}
            />
            <SegmentedControl
              options={CAMPAIGN_BOARD_VIEWS.map((view) => ({
                value: view.key,
                label: view.label,
                icon:
                  view.key === "queue" ? (
                    <ListIcon className="size-4" aria-hidden="true" />
                  ) : (
                    <LayoutGrid className="size-4" aria-hidden="true" />
                  ),
              }))}
              value={boardView}
              onChange={setBoardView}
            />
          </div>
        </div>

        {plannedPosts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              No planned posts yet.
            </CardContent>
          </Card>
        ) : filteredPosts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              No posts match this filter.
            </CardContent>
          </Card>
        ) : boardView === "queue" ? (
          <div className="space-y-3">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="border-border/60 bg-card/95 shadow-sm">
                <CardContent className="grid gap-4 p-4 md:grid-cols-[5.5rem_minmax(0,1fr)_auto] md:items-center">
                  <QueuePostThumbnail post={post} />
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill
                        label={PLANNED_POST_STATUS_CONFIG[post.status]?.label ?? post.status}
                        className={PLANNED_POST_STATUS_CONFIG[post.status]?.className}
                      />
                      <span className="text-xs uppercase tracking-[0.14em] text-foreground/50">
                        {formatDate(post.scheduledFor)}
                      </span>
                      <Badge variant="outline" className="rounded-full text-[11px]">
                        {getPostTypeLabel(post.slot.postType)}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-foreground">
                        {post.slot.concept}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm leading-6 text-foreground/64">
                        {post.slot.callToAction ?? post.slot.objectiveLabel ?? post.slot.objective}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedScheduledPostId(post.id);
                        setNewAttemptSheetOpen(true);
                      }}
                    >
                      Regenerate
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(buildPlannedPostDetailPath(brandId, campaignId, post.id))
                      }
                    >
                      Open Workspace
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="columns-1 gap-6 md:columns-2 2xl:columns-3">
            {filteredPosts.map((post) => (
              <div key={post.id} className="mb-6 break-inside-avoid">
                <PlannedPostCard
                  post={post}
                  selected={false}
                  expanded={expandedPostIds.includes(post.id)}
                  longDateLabel={formatDate(post.scheduledFor)}
                  onToggleDetails={() => {
                    setExpandedPostIds((current) =>
                      current.includes(post.id)
                        ? current.filter((value) => value !== post.id)
                        : [...current, post.id],
                    );
                  }}
                  onOpenWorkspace={() =>
                    navigate(buildPlannedPostDetailPath(brandId, campaignId, post.id))
                  }
                  onRegenerate={() => {
                    setSelectedScheduledPostId(post.id);
                    setNewAttemptSheetOpen(true);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <CampaignOverviewSection campaignBrief={campaignBrief} />

      <section className="space-y-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">Campaign And Copywriter Prompts</h2>
          <p className="max-w-3xl text-[15px] leading-7 text-foreground/68">
            Materialized prompts captured from the LLM calls that planned this campaign and wrote
            publish copy for its posts.
          </p>
        </div>
        <MaterializedPromptPanel prompts={campaign.materializedPrompts} />
      </section>
    </div>
  );
}

function QueuePostThumbnail({ post }: { post: CampaignReviewPlannedPost }) {
  const latestCompleted = post.output.latestCompleted;
  const plan = latestCompleted?.result?.plan ?? null;
  const brandkit = latestCompleted?.preview?.brandkit ?? null;
  const previewAsset = latestCompleted?.assets[0] ?? null;

  return (
    <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border bg-muted/30">
      {plan && brandkit ? (
        <div className="w-24 scale-[0.22]">
          <SceneTemplatePreview
            plan={plan}
            linkedAssets={latestCompleted?.assets ?? []}
            brandkit={brandkit}
            showCanvasMeta={false}
            maxWidth={360}
          />
        </div>
      ) : previewAsset ? (
        <img
          src={previewAsset.signedUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <ImageIcon className="size-5 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );
}
